import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createTheme, CssBaseline, alpha} from '@mui/material';
import { Box } from '@mui/system';

import { DarkTheme, LightTheme, AppThemeName } from './../themes';

interface IThemeContextData {
  themeName: AppThemeName;
  setThemeName: (name: AppThemeName) => void;
  temaSistema: 'light' | 'dark';
}

const THEME_STORAGE_KEY = 'APP_THEME';
const VALID_THEMES: AppThemeName[] = ['light', 'dark', 'blue'];

const ThemeContext = createContext({} as IThemeContextData);

export const useAppThemeContext = () => {
  return useContext(ThemeContext);
};

interface IAppThemeProviderProps {
  children: React.ReactNode;
}
export const AppThemeProvider: React.FC<IAppThemeProviderProps> = ({ children }) => {
  const [themeName, setThemeNameState] = useState<AppThemeName>(() => {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) as AppThemeName | null;
    return storedTheme && VALID_THEMES.includes(storedTheme) ? storedTheme : 'light';
  });

  const mode: 'light' | 'dark' = themeName === 'dark' ? 'dark' : 'light';

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, themeName);
  }, [themeName]);

  const setThemeName = useCallback((name: AppThemeName) => {
    setThemeNameState(name);
  }, []);

  const theme = useMemo(() => {
    const baseTheme = mode === 'light' ? LightTheme : DarkTheme;

    return createTheme({
      ...baseTheme,

      breakpoints: {
        values: {
          xs: 0,
          sm: 700,
          md: 900,
          lg: 1200,
          xl: 1536,
        },
      },

      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              scrollbarWidth: 'thin',

              scrollbarColor:
                mode === 'dark'
                  ? 'rgba(255,255,255,0.20) transparent'
                  : 'rgba(0,0,0,0.20) transparent',

              '&::-webkit-scrollbar': {
                width: '6px',
                height: '6px',
              },

              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },

              '&::-webkit-scrollbar-thumb': {
                backgroundColor:
                  mode === 'dark'
                    ? alpha('#fff', 0.18)
                    : alpha('#000', 0.18),

                borderRadius: '10px',
              },

              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor:
                  mode === 'dark'
                    ? alpha('#fff', 0.30)
                    : alpha('#000', 0.30),
              },
            },
          },
        },
      },
    });
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ themeName, setThemeName, temaSistema: mode }}>
      <ThemeProvider theme={theme}>
      <CssBaseline />
        <Box width="100vw" height="100vh" bgcolor={theme.palette.background.default}>
          {children}
        </Box>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
